/**
 * hooks/useLockScene.ts — 3D scene orchestration (PLAN §2.17, T13).
 *
 * REACTIVE hook which makes up the four ingredients of the constraint system:
 *
 * ```
 * Form (react-hook-form, lock calculator FormProvider)
 * ↓ useWatch (fields entering 3D)
 * data/useFeatures (API) → GlbFeatures[] (one per connector)
 * hooks/useAssets (useLockAssets) → GLB URL blob (lock + fasteners)
 * ↓ useMemo
 * model/resolveScene (STEP CHAIN 0→6) → ResolvedScene (objects ALREADY resolved)
 * ↓
 * LockScene renders MUTI components (T14/T15)
 * ```
 *
 * Boundary Rules (SPEC §6, PLAN §1.2):
 * - No side-effects here: pure `useMemo`, the form is observed via useWatch.
 * - MUST be used INSIDE the lock calculator's `FormProvider`
 * (react-hook-form): without context `useFormContext` launches.
 * (In T15 it is called OUTSIDE r3f's <Canvas> — React contexts are not
 * propagated within the Canvas; the resolved scene passes as props.)
 * - Don't compute positions: delegate entirely to `model/resolveScene`.
 *
 * Constrained 3D configurator: each step is commented for maintainability.*/

import { useMemo } from "react";
import { useFeatures } from "../data/useFeatures";
import { useLockstopFeatures } from "../data/useLockstopFeatures";
import type { ResolvedScene } from "../model/types";
import { resolveScene } from "../model/resolveScene";
import type { ResolveSceneFormInput } from "../model/resolveScene";
import { computeLockstopInstances } from "../model/lockstopPlacement";
import type { LockstopInstance } from "../model/lockstopPlacement";
import { lockstopKindForCode } from "../config/lockstopKinds";
import {
  CONNECTOR_PLANE_HEIGHT_MM,
  DEFAULT_FASTENER_LENGTH_MM,
  OFFSET_FROM_TOP,
} from "../config/defaults";
import { useLockAssets } from "./useAssets";
import type { LockAssets } from "./useAssets";
import type { LockSceneInput } from "../LockScene.types";

//─── Return type (PLAN §2.17) ─────────────────────────────

/** Full data for the 3D scene, produced by the orchestration hook.*/
export interface LockSceneData {
  /** Scene resolved (objects ALREADY placed, mm) or null if form not ready.*/
  scene: ResolvedScene | null;
  /** GLB asset URL blobs (locks + fasteners) — aligned to scenes.*/
  assets: LockAssets;
  /**
   * Istanze fisiche del lockstop, già risolte (28-09-2026) — vuoto se non
   * selezionato, non ancora mappato (`lockstopInstallationLevels`
   * mancante), o codice non in catalogo (`lockstopKindForCode` → null).*/
  lockstops: LockstopInstance[];
  /** true if the scene has meaningful data (false if form not yet initialized).*/
  hasData: boolean;
}

//─── Constants shared with resolveScene (config) ─────────────

/**
 * Constant configuration passed to `resolveScene` (PLAN §2.15 `config`).
 * In v1 it all comes from `config/defaults.ts`; kept here as a single point
 * calibration (SPEC §6: config/ contains every constant).*/
const RESOLVE_CONFIG = {
  planeHeight: CONNECTOR_PLANE_HEIGHT_MM,
  offsetsFromTop: { header: OFFSET_FROM_TOP.header, joist: OFFSET_FROM_TOP.joist },
  defaultFastenerLength: DEFAULT_FASTENER_LENGTH_MM,
} as const;

// ─── Hook ─────────────────────────────────────────────────────

/**
 * Orchestration reactive hook (PLAN §2.17, T13).
 *
 * Reads the form parameters that enter the 3D (SPEC §3.2), loads the
 * features (API) and GLB assets, and resolves the scene in `useMemo`.
 *
 * `lockstopId` field: used for the features/GLB fetch (`useLockstopFeatures`,
 * `useLockAssets`) — does NOT enter `resolveScene` (the connector's model/
 * stays ignorant of the lockstop, SPEC §6), its geometry is composed
 * SEPARATELY, after `resolveScene`, in `model/lockstopPlacement.ts` (28-09-2026),
 * using the SAME already-resolved `scene.connectors`.
 * `connectorPairLockItemIds` (first/second connector) is for lock selection only
 * features (API) and GLB assets, not for chain geometry.
 *
 * @param lockstopOptions Dati di CATALOGO del lockstop scelto (livelli di
 * installazione + codice) — dalla calculation già caricata, NON dal form
 * live (stesso motivo di `serviceClass`/`loads` in LockSceneProps): il
 * form sa solo l'item id (`input.lockstopItemId`, per il fetch del GLB),
 * non la riga di `lock_lockstop_mapping` per la coppia scelta.
 * @returns LockSceneData with `scene` (null if uninitialized form),
 * `assets`, `lockstops` and `hasData` consistent.*/
export function useLockScene(
  input: LockSceneInput,
  lockstopOptions: {
    installationLevels: number | null;
    code: string | null;
    screwEdgeThicknessMm: number | null;
  } = { installationLevels: null, code: null, screwEdgeThicknessMm: null },
): LockSceneData {
  // ─── 1. Features (API, one entry for each connector pair) ───────
  const { features: featuresList, isLoading: featuresLoading } = useFeatures([
    ...input.connectorPairLockItemIds,
  ]);

  // Wider lock for the header half only: its own fetch, since `featuresList` drops missing entries
  // and would misalign the pair indexes.
  const { features: headerLockFeatures, isLoading: headerFeaturesLoading } = useFeatures([
    input.headerLockItemId ?? null,
  ]);

  // ─── 2. Lockstop features (API, un solo lockstop per calcolo) ───
  const { features: lockstopFeatures } = useLockstopFeatures(input.lockstopItemId);

  // ─── 3. Asset GLB (hooks/useAssets) ─────────────────────────────
  //Look at the form selections and load the URL blobs (global cache).
  const assets = useLockAssets(input);

  //─── 4. Resolve the scene (model/resolveScene, CHAIN ​​STEP 0→6) ──
  const { scene, hasData } = useMemo<{ scene: ResolvedScene | null; hasData: boolean }>(() => {
    // Sub-set of the semantic input that enters the constraint system.
    const formInput: ResolveSceneFormInput = {
      lateralResistanceConfig: input.lateralResistanceConfig,
      headerOrientation: input.headerOrientation,
      nConnectors: input.connectorPairCount,
      headerB: input.header.width,
      headerH: input.header.height,
      joistB: input.joist.width,
      joistH: input.joist.height,
      alphaDeg: input.joist.alphaDeg,
      betaDeg: input.joist.betaDeg,
      connectorTopDistanceMm: input.joist.connectorTopDistance,
      joistTopOffsetMm: input.joist.topOffsetFromHeader,
      headFastenerItemId: input.fasteners.headerItemId,
      joistFastenerItemId: input.fasteners.joistItemId,
      inclinedFastenerItemId: input.fasteners.inclinedItemId,
      headFastenerLengthMm: input.fasteners.headerLengthMm ?? null,
      joistFastenerLengthMm: input.fasteners.joistLengthMm ?? null,
      inclinedFastenerLengthMm: input.fasteners.inclinedLengthMm ?? null,
      headFastenerDiameterMm: input.fasteners.headerDiameterMm ?? null,
      joistFastenerDiameterMm: input.fasteners.joistDiameterMm ?? null,
      inclinedFastenerDiameterMm: input.fasteners.inclinedDiameterMm ?? null,
      headFastenerCode: input.fasteners.headerCode ?? null,
      joistFastenerCode: input.fasteners.joistCode ?? null,
      inclinedFastenerCode: input.fasteners.inclinedCode ?? null,
    };

    const resolved = resolveScene({
      featuresList,
      glbUrls: [assets.connGlbUrl1, assets.connGlbUrl2],
      //P6: GLB of fasteners (from useLockAssets), aligned to fastenerItemId.
      fastenerGlbUrls: {
        head: assets.headFastenerGlbUrl,
        joist: assets.joistFastenerGlbUrl,
        inclined: assets.inclinedFastenerGlbUrl,
      },
      headerOverride: headerLockFeatures[0]
        ? { features: headerLockFeatures[0], glbUrl: assets.headerLockGlbUrl }
        : null,
      headerLockCode: input.headerLockCode ?? null,
      itemIds: [...input.connectorPairLockItemIds],
      itemCodes: [...(input.connectorPairCodes ?? [null, null])],
      form: formInput,
      config: RESOLVE_CONFIG,
    });

    return { scene: resolved, hasData: resolved.hasData };
    //Dependencies: the observed form (new object with each modification),
    //the features (change only with itemIds) and the GLB url.
  }, [input, featuresList, headerLockFeatures, assets.connGlbUrl1, assets.connGlbUrl2, assets.headerLockGlbUrl]);

  //─── 5. Lockstop instances (model/lockstopPlacement, DOPO resolveScene) ──
  //Composta qui, non dentro resolveScene (SPEC §6: il model/ del connettore
  //resta ignaro del lockstop) — usa `scene.connectors` GIA' risolti, stesso
  //confine di `computeAssemblyMidpoint` per le frecce di carico.
  const lockstops = useMemo<LockstopInstance[]>(() => {
    if (!scene || !lockstopFeatures) return [];
    const kind = lockstopKindForCode(lockstopOptions.code);
    if (!kind) return [];
    const installationLevels = lockstopOptions.installationLevels === 2 ? 2 : 1;
    return computeLockstopInstances({
      features: lockstopFeatures,
      installationLevels,
      kind,
      connectors: scene.connectors,
      glbUrl: assets.lockstopGlbUrl,
      screwEdgeThicknessMm: lockstopOptions.screwEdgeThicknessMm,
    });
  }, [
    scene,
    lockstopFeatures,
    lockstopOptions.code,
    lockstopOptions.installationLevels,
    lockstopOptions.screwEdgeThicknessMm,
    assets.lockstopGlbUrl,
  ]);

  // Durante il fetch `featuresList` e' vuoto ma non e' un esito: senza questo
  // guard LockSceneCapture scatterebbe sulle sole travi, senza connettori.
  return { scene, assets, lockstops, hasData: hasData && !featuresLoading && !headerFeaturesLoading };
}
